import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X } from 'lucide-react';
import { Form, FormField } from '@/firebase/formService';
import { formCompletionService, TableFormItem } from '@/firebase/formCompletionService';
import { locationService, Location } from '@/firebase/locationService';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { fetchUserRoles, getUserSystemRoleForOrganization, isSystemUserInOrganization } from '@/utils/roleHelpers';
import { analyzeFormCompletion } from '@/services/geminiService';
import { complianceService } from '@/firebase/complianceService';
import { trackForm } from '@/utils/analytics';
import { evaluateFormula, type FormulaContext } from '@/utils/formulaEvaluator';
import { getUserPreferredLanguage, getFormName, getFieldLabel, getItemNames, getFormDescription } from '@/utils/formLanguageHelper';

interface RunTableFormProps {
  form: Form;
  assetId?: string;
  onClose: () => void;
  onComplete: () => void;
}

const RunTableForm: React.FC<RunTableFormProps> = ({ form, assetId, onClose, onComplete }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const preferredLanguage = getUserPreferredLanguage(user);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [currentForm, setCurrentForm] = useState<Form>(form);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  
  // Table form state
  const [tableItemValues, setTableItemValues] = useState<Record<string, Record<string, any>>>({}); // item -> field label -> value
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get items list (localized)
  const items = useMemo(() => {
    return getItemNames(currentForm.itemsDefinition, preferredLanguage);
  }, [currentForm.itemsDefinition, preferredLanguage]);

  // Fetch fresh form data when form.id changes to ensure we have latest locationIds
  useEffect(() => {
    const fetchFreshForm = async () => {
      if (form?.id) {
        try {
          const { formService } = await import('@/firebase/formService');
          const result = await formService.getForm(form.id);
          if (result.success && result.data) {
            setCurrentForm(result.data);
            setIsFormLoaded(true);
          } else {
            setCurrentForm(form);
            setIsFormLoaded(true);
          }
        } catch (error) {
          console.error('Error fetching fresh form:', error);
          setCurrentForm(form);
          setIsFormLoaded(true);
        }
      } else {
        setCurrentForm(form);
        setIsFormLoaded(true);
      }
    };
    
    setIsFormLoaded(false);
    fetchFreshForm();
  }, [form?.id]);

  const loadLocations = useCallback(async () => {
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          if (currentOrganization?.id) {
            userRole = getUserSystemRoleForOrganization(userData, currentOrganization.id) || userData.role;
            setIsSystemUser(isSystemUserInOrganization(userData, currentOrganization.id));
          } else {
            userRole = userData.role;
            const roles = await fetchUserRoles(user.id);
            setIsSystemUser(roles.isSystemUser);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    const result = await locationService.getAccessibleLocations(
      currentOrganization?.id,
      user?.id,
      userRole,
      userLocationIds
    );
    if (result.success && result.data) {
      let validLocations = result.data.filter(loc => loc.id && loc.name);
      
      // If form has locationIds specified, filter to only show those locations
      const shouldFilter = currentForm?.locationIds && Array.isArray(currentForm.locationIds) && (currentForm.locationIds.length ?? 0) > 0;
      
      if (shouldFilter) {
        validLocations = validLocations.filter(loc => 
          loc.id && currentForm.locationIds!.includes(loc.id)
        );
      }
      
      setAvailableLocations(validLocations);
      setUserLocationIds(userLocationIds || []);
    }
  }, [currentForm, user, currentOrganization]);

  useEffect(() => {
    if (isFormLoaded) {
      loadLocations();
      initializeForm();
      if (currentForm?.id) {
        trackForm.started(currentForm.id);
      }
    }
  }, [isFormLoaded, currentForm, loadLocations]);

  useEffect(() => {
    if (availableLocations.length === 1 && !selectedLocation) {
      setSelectedLocation(availableLocations[0]);
    }
  }, [availableLocations.length, selectedLocation]);


  const initializeForm = () => {
    const vars: Record<string, string> = {};
    const headerVals: Record<string, any> = {};
    const itemVals: Record<string, Record<string, any>> = {};

    // Initialize variables
    if (currentForm.variables) {
      currentForm.variables.forEach((variable) => {
        if (typeof variable === 'string') {
          vars[variable] = '';
        } else {
          vars[variable.name] = '';
        }
      });
    }

    // Initialize item values for each item
    items.forEach(item => {
      itemVals[item] = {};
      currentForm.tableFields?.forEach((field) => {
        if (field.type === 'reference') {
          // Use per-item reference value
          const refValue = field.itemReferenceValues?.[item];
          itemVals[item][field.label] = refValue !== undefined ? refValue : '';
        } else if (field.type === 'calculated') {
          itemVals[item][field.label] = ''; // Will be calculated
        } else if (field.type === 'checkbox') {
          itemVals[item][field.label] = false;
        } else {
          itemVals[item][field.label] = field.defaultValue || '';
        }
      });
    });

    setVariableValues(vars);
    setTableItemValues(itemVals);
  };

  // Create hash of non-calculated field values for dependency tracking
  const nonCalculatedValuesHash = useMemo(() => {
    const values: Record<string, any> = {};
    items.forEach(item => {
      form.tableFields?.forEach(field => {
        if (field.type !== 'calculated') {
          const key = `${item}_${field.label}`;
          if (field.type === 'reference') {
            // Use per-item reference value if available
            const refValue = field.itemReferenceValues?.[item];
            values[key] = refValue;
          } else {
            values[key] = tableItemValues[item]?.[field.label];
          }
        }
      });
    });
    return JSON.stringify(values);
  }, [tableItemValues, items, form.tableFields]);

  // Recalculate calculated fields when values change
  useEffect(() => {
    if (!form.tableFields || items.length === 0) return;

    const calculatedFields = form.tableFields.filter(f => f.type === 'calculated' && f.formula);
    if (calculatedFields.length === 0) return;

    const updatedItemValues: Record<string, Record<string, any>> = { ...tableItemValues };
    let hasChanges = false;

    items.forEach(item => {
      const itemFieldValues = { ...(tableItemValues[item] || {}) };
      
      // Include reference field values for this item
      form.tableFields?.forEach(field => {
        if (field.type === 'reference') {
          // Use per-item reference value
          const refValue = field.itemReferenceValues?.[item];
          if (refValue !== undefined) {
            itemFieldValues[field.label] = refValue;
          }
        }
      });
      
      // Build formula context for this item (include item's field values including reference values)
      const context: FormulaContext = {
        fieldValues: {
          ...itemFieldValues // Include item's field values (including reference values for this item)
        },
        fields: form.tableFields || []
      };

      calculatedFields.forEach(field => {
        if (field.formula) {
          try {
            const result = evaluateFormula(field.formula, context);
            if (itemFieldValues[field.label] !== result) {
              itemFieldValues[field.label] = result;
              hasChanges = true;
            }
          } catch (error: any) {
            console.error(`Error calculating field "${field.label}" for item "${item}":`, error);
            if (itemFieldValues[field.label] !== `Error: ${error.message}`) {
              itemFieldValues[field.label] = `Error: ${error.message}`;
              hasChanges = true;
            }
          }
        }
      });

      updatedItemValues[item] = itemFieldValues;
    });

    if (hasChanges) {
      setTableItemValues(updatedItemValues);
    }
  }, [nonCalculatedValuesHash, items, form.tableFields]);

  const handleItemFieldChange = (item: string, fieldLabel: string, value: any) => {
    setTableItemValues(prev => ({
      ...prev,
      [item]: {
        ...(prev[item] || {}),
        [fieldLabel]: value
      }
    }));
  };


  const renderTableCell = (item: string, field: FormField) => {
    const value = tableItemValues[item]?.[field.label];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <input
            type={field.type}
            value={value || ''}
            onChange={(e) => handleItemFieldChange(item, field.label, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            required={field.required}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleItemFieldChange(item, field.label, e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={field.placeholder}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );
      
      case 'reference':
        return (
          <div className="px-2 py-1 text-xs bg-gray-50 border border-gray-300 rounded">
            <span className="text-gray-700 font-medium">
              {field.itemReferenceValues?.[item] !== undefined ? field.itemReferenceValues[item] : 'N/A'}
            </span>
          </div>
        );
      
      case 'calculated':
        const calculatedValue = value;
        const hasError = typeof calculatedValue === 'string' && calculatedValue.startsWith('Error:');
        return (
          <div className={`px-2 py-1 text-xs border rounded ${
            hasError ? 'bg-red-50 border-red-300 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            {calculatedValue !== undefined && calculatedValue !== null ? String(calculatedValue) : '...'}
          </div>
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleItemFieldChange(item, field.label, e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            required={field.required}
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      default:
        return <div className="text-xs text-gray-400">Type: {field.type}</div>;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      showNotification('Please select a location', 'error');
      return;
    }

    // Validate table fields per item
    for (const item of items) {
      form.tableFields?.forEach(field => {
        if (field.required && field.type !== 'reference' && field.type !== 'calculated') {
          const value = tableItemValues[item]?.[field.label];
          if (value === undefined || value === null || value === '' || 
              (field.type === 'checkbox' && value === false)) {
            showNotification(`Please fill in required field "${field.label}" for item "${item}"`, 'error');
            return;
          }
        }
      });
    }

    setIsSubmitting(true);

    try {
      // Build table items array
      const tableItems: TableFormItem[] = items.map(item => ({
        item,
        fieldValues: { ...tableItemValues[item] }
      }));

      // Include reference field values (per-item)
      form.tableFields?.forEach(field => {
        if (field.type === 'reference') {
          tableItems.forEach(tableItem => {
            // Use per-item reference value
            const refValue = field.itemReferenceValues?.[tableItem.item];
            if (refValue !== undefined) {
              tableItem.fieldValues[field.label] = refValue;
            }
          });
        }
      });

      // Include calculated field values (skip errors)
      form.tableFields?.forEach(field => {
        if (field.type === 'calculated') {
          tableItems.forEach(tableItem => {
            const calculatedValue = tableItemValues[tableItem.item]?.[field.label];
            if (calculatedValue !== undefined && calculatedValue !== null &&
                !(typeof calculatedValue === 'string' && calculatedValue.startsWith('Error:'))) {
              tableItem.fieldValues[field.label] = calculatedValue;
            }
          });
        }
      });

      // Fetch owner_role
      let ownerRole: string | undefined;
      if (form.templateId) {
        try {
          const templateResult = await complianceService.getTemplate(form.templateId);
          if (templateResult.success && templateResult.data) {
            ownerRole = templateResult.data.owner_role;
          }
        } catch (error) {
          console.warn('Error fetching compliance template for owner_role:', error);
        }
      }

      // Generate AI summary (optional, can summarize all items)
      let aiSummary: string | undefined;
      try {
        // Create a summary representation for AI
        const summaryData = {
          items: tableItems.map(ti => ({
            item: ti.item,
            values: ti.fieldValues
          }))
        };
        aiSummary = await analyzeFormCompletion(
          form.name,
          form.tableFields || [],
          summaryData as any
        );
      } catch (error) {
        console.error('Error generating AI summary:', error);
        aiSummary = undefined;
      }

      const result = await formCompletionService.addCompletion({
        formId: form.id || '',
        formName: form.name,
        locationId: selectedLocation.id!,
        locationName: selectedLocation.name,
        assetId: assetId,
        variableValues: variableValues,
        fieldValues: {}, // Empty for table forms
        isTableForm: true,
        tableItems: tableItems,
        aiSummary: aiSummary?.trim(),
        completedBy: user ? `${user.name} (${user.email})` : 'Unknown',
        organizationId: currentOrganization?.id,
        owner_role: ownerRole,
      });

      if (result.success) {
        trackForm.completed(form.id || '');
        showNotification('Table form submitted successfully!', 'success');
        onComplete();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error submitting table form:', error);
      showNotification('Failed to submit form', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Error</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            This table form has no items defined. Please edit the form to add items.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{getFormName(currentForm, preferredLanguage)}</h2>
            {getFormDescription(form, preferredLanguage) && (
              <p className="text-xs text-gray-600 mt-1">{getFormDescription(form, preferredLanguage)}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Location Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Location <span className="text-red-600">*</span>
            </label>
            <select
              value={selectedLocation?.id || ''}
              onChange={(e) => {
                const location = availableLocations.find(l => l.id === e.target.value);
                setSelectedLocation(location || null);
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Select a location...</option>
              {availableLocations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </div>

          {/* Variables */}
          {form.variables && form.variables.length > 0 && (
            <div className="space-y-2">
              {form.variables.map((variable, idx) => {
                const varDef = typeof variable === 'string' 
                  ? { name: variable as string, possibleValues: [] }
                  : variable;
                const hasValues = varDef.possibleValues && varDef.possibleValues.length > 0;
                
                return (
                  <div key={idx}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {varDef.name} <span className="text-red-600">*</span>
                    </label>
                    {hasValues ? (
                      <select
                        value={variableValues[varDef.name] || ''}
                        onChange={(e) => {
                          setVariableValues(prev => ({
                            ...prev,
                            [varDef.name]: e.target.value
                          }));
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Select {varDef.name.toLowerCase()}...</option>
                        {varDef.possibleValues.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={variableValues[varDef.name] || ''}
                        onChange={(e) => {
                          setVariableValues(prev => ({
                            ...prev,
                            [varDef.name]: e.target.value
                          }));
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        required
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Items</h3>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 sticky left-0 bg-gray-50 z-10 min-w-[120px]">
                        Item
                      </th>
                      {form.tableFields?.map((field, idx) => {
                        const displayLabel = getFieldLabel(field, preferredLanguage);
                        return (
                          <th key={idx} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 min-w-[150px]">
                            {displayLabel}
                            {field.required && <span className="text-red-600 ml-1">*</span>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item, itemIdx) => (
                      <tr key={itemIdx} className={itemIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-300 sticky left-0 bg-inherit z-10">
                          {item}
                        </td>
                        {form.tableFields?.map((field, fieldIdx) => (
                          <td key={fieldIdx} className="px-2 py-1.5 border-r border-gray-200">
                            {renderTableCell(item, field)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Table Form'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RunTableForm;

